import AgencyHelper from 'features/Agency/agencyHelper';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

DialogAdminApprovePaymentView.propTypes = {
    item: PropTypes.object,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

function DialogAdminApprovePaymentView({ item, onCancel, onDone }) {
    // MARK: ---- PROPERTIES ----
    const [loading, setLoading] = useState(false);
    const [readyToValidate, setReadyToValidate] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [amount, setAmount] = useState(0);
    const [tax, setTax] = useState('');
    const [vat, setVat] = useState('');
    const [totalAmount, setTotalAmount] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState('PROCESSING');
    const [paymentDocument, setPaymentDocument] = useState([]);
    const paymentDocumentPathList = useRef([]);

    useEffect(() => {
        if (!item) return;

        const amount = item.amount ?? 0;
        const taxRate = item.tax ?? 0;
        const vatRate = item.vat ?? 0;

        setPaymentMethod(amount === 0 ? 'FREE' : 'NOT_FREE');
        setAmount(amount);
        setTax(Math.floor(taxRate * 100));
        setVat(Math.floor(vatRate * 100));

        const total = Math.floor(amount * (1 + taxRate + vatRate));
        setTotalAmount(total);

        setPaymentStatus(amount === 0 ? 'COMPLETED' : 'PROCESSING');

        setPaymentDocument(Array.isArray(item.paymentDocument) ? item.paymentDocument : []);
    }, [item]);

    // MARK: ---- FUNCTIONS ----

    async function uploadAsyncFunction(file) {
        return new Promise((resolve) => {
            if (file instanceof File) {
                miscHelper.callApiUploadFile(file).then((mediaList) => {
                    if (_.isArray(mediaList) && mediaList.length > 0) {
                        const firstMedia = _.first(mediaList);
                        const relativePath = firstMedia?.relativePath;
                        if (relativePath) {
                            resolve(relativePath);
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            } else {
                resolve(file);
            }
        });
    }

    const handleSubmit = async () => {
        setReadyToValidate(true);
        if (amount > 0 && !paymentDocument?.length) {
            ToastHelper.showError('Vui lòng tải lên chứng từ thanh toán');
            return;
        }

        setLoading(true);

        if (!_.isEmpty(paymentDocument)) {
            paymentDocumentPathList.current = [];
            await processSelectedFiles(paymentDocument);
        }

        let params = {
            paymentStatus,
            paymentDocument: paymentDocumentPathList.current,
        };
        params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');

        const res = await MaintainerHelper.API.requestUpdateTicket(item.ticketId, params);
        if (_.isBoolean(res) && res) {
            ToastHelper.showSuccess('Duyệt thanh toán thành công');
            onDone && onDone();
        } else if (_.isString(res)) {
            ToastHelper.showError(res);
        }
        setLoading(false);
    };

    const handleFilesChanged = (e) => {
        const newFiles = Array.from(e.target.files || []);
        if (!newFiles.length) return;

        setPaymentDocument((prev) => {
            const validNew = newFiles.filter((file) => {
                if (file.name.length > 128) {
                    ToastHelper.showError(`Tên file quá dài! Tối đa cho phép 128 ký tự. (> 128 ký tự)!`);
                    return false;
                }
                return true;
            });

            if (!validNew.length) return prev;

            const all = [...prev, ...validNew];

            return all.filter((file, idx, arr) =>
                idx === arr.findIndex((f) => {
                    if (typeof f === 'string' && typeof file === 'string') return f === file;
                    if (f instanceof File && file instanceof File) {
                        return f.name === file.name && f.size === file.size;
                    }
                    return false;
                })
            );
        });

        e.target.value = null;
    };

    const handleRemoveFile = (file) => {
        setPaymentDocument(prevFiles =>
            prevFiles.filter(item =>
                typeof file === 'string'
                    ? item !== file
                    : !(item instanceof File && item.lastModified === file.lastModified)
            )
        );
    };

    async function processSelectedFiles(files) {
        for (const file of files) {
            const path = await uploadAsyncFunction(file);
            paymentDocumentPathList.current?.push(path);
        }
    }
    async function uploadAsyncFunction(file) {
        return new Promise((resolve) => {
            if (file instanceof File) {
                miscHelper.callApiUploadFile(file).then((mediaList) => {
                    if (_.isArray(mediaList) && mediaList.length > 0) {
                        const firstMedia = _.first(mediaList);
                        const relativePath = firstMedia?.relativePath;
                        if (relativePath) {
                            resolve(relativePath);
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            } else {
                resolve(file);
            }
        });
    }

    const paymentMethodOptions = [
        { name: 'Miễn phí', value: 'FREE' },
        { name: 'Có phí', value: 'NOT_FREE' },
    ]

    return (
        <div>
            <div className="row m-0 p-0 px-2 mt-4">
                {/* paymentMethod */}
                <div className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Hình thức tính phí', true)}
                    <KTBSDropdown
                        toggleElement={({ showError }) => (
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between disabled`}
                                type="button"
                            >
                                {_.find(
                                    paymentMethodOptions,
                                    (item) => item.value === paymentMethod
                                )?.name ?? 'Chọn hình thức tính phí'}
                            </button>
                        )}
                        selectedValue={paymentMethod}
                        dropdownMenuItems={paymentMethodOptions}
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        dropdownMenuClassName="overflow-auto w-100"
                        onChange={(value) => {
                            setPaymentMethod(value);
                            if (value === 'FREE') {
                                setAmount(0);
                            }
                        }}
                        enableCheckValid
                        isDropdownForm={true}
                        isValid={Utils.isValid(paymentMethod)}
                        isTouched={readyToValidate}
                        feedbackText={readyToValidate && !Utils.isValid(paymentMethod) ? "Vui lòng chọn hình thức tính phí" : ''}
                        disabled={true}
                    />
                </div>
                {/* amount */}
                <td className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Chi phí bảo trì/sửa chữa', true)}
                    <KTFormInput
                        name="amount"
                        placeholder="Nhập chi phí bảo trì/sửa chữa"
                        type={KTFormInputType.currency}
                        value={amount}
                        onChange={(value) => {
                            setAmount(Math.floor(value));
                            setTotalAmount(Math.floor(value * (1 + tax / 100 + vat / 100)));
                        }}
                        disabled={true}
                    />
                </td>
                {/* tax */}
                <div className="col-md-6 mb-4 px-2">
                    {UIHelper.buildFormLabel('Thuế TNCN (%)', true)}
                    <KTBSDropdown
                        name="tax"
                        toggleElement={
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between disabled`}
                                type="button"
                            >
                                {
                                    _.find(
                                        AgencyHelper.Data.orderTaxRateOptions,
                                        (taxRate) => taxRate.value == tax
                                    )?.name || "Chọn thuế suất"
                                }
                            </button>
                        }
                        selectedValue={tax}
                        dropdownMenuItems={AgencyHelper.Data.orderTaxRateOptions}
                        dropdownMenuClassName="overflow-auto w-100 position-relative"
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        onChange={(value) => {
                            setTax(Math.floor(value));
                            setTotalAmount(Math.floor(amount * (1 + value / 100 + vat / 100)));
                        }}
                        isDropdownForm={true}
                        disabled={true}
                    />
                </div>
                {/* vat */}
                <div className="col-md-6 mb-4 px-2">
                    {UIHelper.buildFormLabel('Thuế VAT (%)', true)}
                    <KTBSDropdown
                        name="tax"
                        toggleElement={
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between disabled`}
                                type="button"
                            >
                                {
                                    _.find(
                                        AgencyHelper.Data.orderTaxRateOptions,
                                        (taxRate) => taxRate.value == vat
                                    )?.name || "Chọn thuế suất"
                                }
                            </button>
                        }
                        selectedValue={vat}
                        dropdownMenuItems={AgencyHelper.Data.orderTaxRateOptions}
                        dropdownMenuClassName="overflow-auto w-100 position-relative"
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        onChange={(value) => {
                            setVat(Math.floor(value));
                            setTotalAmount(Math.floor(amount * (1 + tax / 100 + value / 100)));
                        }}
                        isDropdownForm={true}
                        disabled={true}
                    />
                </div>
                {/* totalAmount */}
                <td className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Tổng chi phí phát sinh', true)}
                    <KTFormInput
                        name="totalAmount"
                        placeholder="Nhập tổng chi phí phát sinh"
                        type={KTFormInputType.currency}
                        value={totalAmount}
                        onChange={(value) => setTotalAmount(value)}
                        disabled={true}
                    />
                </td>
                {/* paymentStatus */}
                <div className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Trạng thái thanh toán', true)}
                    <KTBSDropdown
                        name="tax"
                        toggleElement={
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between
                                       ${readyToValidate && !Utils.isValid(paymentStatus) ? 'button-invalid' : ''}
                                       ${amount === 0 ? 'disabled' : ''}
                                    `}
                                type="button"
                            >
                                {
                                    _.find(
                                        MaintainerHelper.Data.paymentStatusOptions.slice(1),
                                        (item) => item.value == paymentStatus
                                    )?.name || "Chọn trạng thái thanh toán"
                                }
                            </button>
                        }
                        selectedValue={paymentStatus}
                        dropdownMenuItems={MaintainerHelper.Data.paymentStatusOptions.slice(1)}
                        dropdownMenuClassName="overflow-auto w-100 position-relative"
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        onChange={(value) => {
                            setPaymentStatus(value);
                        }}
                        isDropdownForm={true}
                        disabled={amount === 0}
                    />
                </div>
                {/* paymentDocument */}
                <div className="col-12 p-2">
                    {UIHelper.buildFormLabel('Tải lên chứng từ thanh toán', amount > 0)}
                    <div>
                        <div className="">
                            <label className="">
                                {UIHelper.buildNegativeButton(
                                    'Thêm tập tin',
                                    () => { },
                                    <i className="fa-regular fa-upload" />
                                )}
                                <input
                                    type="file"
                                    accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                                    onChange={handleFilesChanged}
                                    multiple
                                    hidden
                                    onClick={(e) => {
                                        e.target.value = null;
                                    }}
                                />
                            </label>
                        </div>
                        {/* danh sach file dang duoc chon */}
                        {!_.isEmpty(paymentDocument) && (
                            <div className="row m-0 p-0">
                                {paymentDocument.map((item, index) => {
                                    return (
                                        <div key={item?.lastModified ?? item ?? index} className='col-6'>
                                            <ImageView
                                                name={item?.lastModified ?? item ?? index}
                                                path={_.isString(item) ? item : item?.name}
                                                onRemove={() => {
                                                    handleRemoveFile(item);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* actions */}
            <div
                className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
                {UIHelper.buildGreenButton(
                    'Duyệt thanh toán',
                    handleSubmit,
                    null,
                    false,
                    loading
                )}
            </div>
        </div>
    );
}

export default DialogAdminApprovePaymentView;
